<template>
    <div class="input-outer-wrapper" :class="wrapperClass" @click="handleClick">
        <span
            class="text-sm font-Inter-Bold lg:text-right text-grey-900 mb-3 inline-flex input-label"
            :class="labelClass"
        >
            {{ label }}
        </span>
        <Dropdown
            ref="dropdown"
            v-model="localValue"
            v-tooltip.bottom="showTooltip && $t('editor.font.size_tooltip')"
            :options="groupedFonts"
            option-label="label"
            option-group-label="items"
            :placeholder="$t('editor.font.placeholder')"
            :option-disabled="(option) => option.group"
            class="w-full font-dropdown"
            :append-to="isCompact ? 'body' : null"
            @show="handleShow"
            @hide="$emit('hide', $event)"
        >
            <template #value="slotProps">
                <span v-if="slotProps.value" :class="[slotProps.value.class, valueTextClass]" class="block">
                    {{ slotProps.value.label }}
                </span>
                <span v-else class="block" :class="!localValue ? 'opacity-40' : valueTextClass"
                    >{{ $t('editor.font.placeholder') }}</span
                >
            </template>
            <template #option="{ option }">
                <div
                    v-if="option.group"
                    class="group-label font-Inter-Bold pointer-events-none border-b border-black text-sm pb-3 border-opacity-10"
                >
                    {{ option.labelKey ? $t(option.labelKey) : option.label }}
                </div>
                <div v-else :class="[option.class, optionTextClass]">
                    {{ option.label }}
                </div>
            </template>
        </Dropdown>
    </div>
</template>

<script>
import { getGroupedFonts } from './fontFamilies';

const EDITOR_FONT_STYLESHEETS = [
    'https://use.typekit.net/idx8cgg.css',
    'https://fonts.googleapis.com/css2?family=Nanum+Myeongjo&family=Old+Standard+TT:wght@700&family=Mrs+Saint+Delafield&family=Sacramento&family=Mr+Dafoe&family=Poiret+One&family=Figtree:ital,wght@0,300..900;1,300..900&family=Fira+Code:wght@300..700&family=Gabarito:wght@400..900&family=Inter+Tight:ital,wght@0,100..900;1,100..900&family=Montserrat:ital,wght@0,400;0,600;0,700;1,400&family=Roboto:ital,wght@0,400;0,500;0,700;1,400&family=Lato:ital,wght@0,400;0,700;1,400&family=Raleway:ital,wght@0,400;0,600;0,700;1,400&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Merriweather:ital,wght@0,400;0,700;1,400&family=Lora:ital,wght@0,400;0,700;1,400&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Kalam:wght@300;400;700&family=Caveat:wght@400;500;600;700&family=Permanent+Marker&family=Shadows+Into+Light&family=Nunito:ital,wght@0,400;0,600;0,700;1,400&family=Poppins:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Source+Sans+Pro:ital,wght@0,400;0,600;0,700;1,400&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Bitter:ital,wght@0,400;0,700;1,400&family=PT+Serif:ital,wght@0,400;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400&family=Dancing+Script:wght@400;500;600;700&family=Indie+Flower&family=Amatic+SC:wght@400;700&family=Comfortaa:wght@300;400;500;600;700&family=Satisfy&display=swap',
    'https://fonts.googleapis.com/css2?family=Work+Sans:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=Manrope:wght@400;500;600;700&family=Outfit:wght@400;500;600;700&family=Ubuntu:ital,wght@0,400;0,500;0,700;1,400&family=Quicksand:wght@400;500;600;700&family=Josefin+Sans:ital,wght@0,400;0,600;0,700;1,400&family=EB+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Serif+Display&family=Fraunces:ital,wght@0,400;0,600;0,700;1,400&family=Noto+Serif:ital,wght@0,400;0,600;0,700;1,400&family=Spectral:ital,wght@0,400;0,600;0,700;1,400&family=Vollkorn:ital,wght@0,400;0,600;0,700;1,400&family=Pacifico&family=Patrick+Hand&family=Gloria+Hallelujah&family=Architects+Daughter&family=Bad+Script&family=Patrick+Hand+SC&family=Oswald:wght@400;500;600;700&family=Anton&family=Bebas+Neue&family=Archivo+Black&family=Righteous&family=Barlow+Condensed:ital,wght@0,400;0,600;0,700;1,400&family=Monoton&family=Orbitron:wght@400;500;600;700;800;900&family=Audiowide&family=Black+Ops+One&family=Teko:wght@300;400;500;600;700&family=Russo+One&display=swap',
];

export default {
    name: 'FontFamilySelector',
    head() {
        return {
            link: EDITOR_FONT_STYLESHEETS.map((href) => ({ rel: 'stylesheet', href })),
        };
    },
    model: {
        prop: 'modelValue',
        event: 'update:modelValue',
    },
    inject: {
        editorFields: { default: null },
    },
    props: {
        modelValue: {
            type: Object,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: true,
        },
        showTooltip: {
            type: Boolean,
            default: false,
        },
        wrapperClass: {
            type: String,
            default: '',
        },
        labelClass: {
            type: String,
            default: '',
        },
        valueTextClass: {
            type: String,
            default: 'text-lg lg:text-xl',
        },
        optionTextClass: {
            type: String,
            default: 'text-lg lg:text-xl',
        },
        scrollOffset: {
            type: Number,
            default: 0,
        },
    },
    emits: ['update:modelValue', 'show', 'hide', 'click'],
    data() {
        return {
            localValue: null,
        };
    },
    computed: {
        // Popups leave the field (to body) in the editors' mobile row, which clips them, and on touch devices.
        isCompact() {
            return this.editorFields ? this.editorFields.isCarousel : Boolean(this.$device && !this.$device.isDesktop);
        },
        groupedFonts() {
            return getGroupedFonts();
        },
    },
    watch: {
        modelValue: {
            handler(newVal) {
                if (!newVal) {
                    if (this.localValue !== null) {
                        this.localValue = null;
                    }
                    return;
                }

                // Find the matching font from groupedFonts by label
                if (newVal.label) {
                    const matched = this.groupedFonts.find((font) => !font.group && font.label === newVal.label);
                    if (matched) {
                        // Check if it's actually different by comparing labels, not references
                        if (!this.localValue || this.localValue.label !== matched.label) {
                            this.localValue = matched;
                        }
                    }
                }
            },
            immediate: true,
        },
        localValue(newVal, oldVal) {
            // Emit whenever localValue changes
            if (newVal) {
                // Only emit if this is actually a change (not the initial setup from modelValue watcher)
                const shouldEmit = !oldVal || oldVal.label !== newVal.label;
                if (shouldEmit) {
                    this.$emit('update:modelValue', newVal);
                }
            } else if (oldVal) {
                // Value was cleared
                this.$emit('update:modelValue', null);
            }
        },
    },
    methods: {
        handleClick(event) {
            this.$emit('click', event);
        },
        handleShow() {
            if (this.$refs.dropdown) {
                this.$emit('show', this.$refs.dropdown);
            }
        },
    },
};
</script>

<style scoped lang="postcss">
.title-typo {
    .p-dropdown-label {
        @apply flex items-center;
        span {
            @apply leading-none flex h-full items-center;
        }
    }
}
</style>
